<script setup lang="ts">
	import { ref, watch } from 'vue'

	import HasPermission from '@/components/HasPermission.vue'
	import ImageCropper from '@/components/ImageCropper.vue'
	import ShadowBox from '@/components/ShadowBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledDropZone from '@/components/StyledDropZone.vue'
	import StyledInput from '@/components/StyledInput.vue'

	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import CoincidencesList from '@/internal/modules/providers/components/CoincidencesList.vue'
	import { Chain } from '@/internal/modules/providers/interfaces/Chain'

	import useChainForm from '../composables/useChainForm'

	interface Props {
		chain: Chain
	}

	const props = defineProps<Props>()

	const {
		handleEvaluationEdit,
		isModifying,
		form,
		isCoincidence,
		updateChainLoading,
		createChainMediaLoading,
		chains,
		handleEdit,
		actionEdit,
	} = useChainForm(props.chain)

	const { hasPermissionUser } = useHasPermissionUser()

	// Image Cropper state
	const showCropper = ref(false)
	const imageToCrop = ref<string>('')
	const tempImageFile = ref<File | null>(null)

	// Watch para detectar cambios en form.logo
	watch(
		() => form.logo,
		(newLogo) => {
			if (
				newLogo &&
				newLogo instanceof File &&
				newLogo !== tempImageFile.value
			) {
				tempImageFile.value = newLogo
				const reader = new FileReader()
				reader.onload = (e) => {
					imageToCrop.value = e.target?.result as string
					showCropper.value = true
				}
				reader.readAsDataURL(newLogo)
			}
		}
	)

	// Manejar cuando se aplica el recorte
	const handleCropApplied = async (croppedFile: File) => {
		tempImageFile.value = croppedFile
		form.logo = croppedFile
		showCropper.value = false

		// Hacer la petición automáticamente después de aplicar el recorte
		await handleEvaluationEdit()
	}

	// Manejar cuando se cancela el recorte
	const handleCropCancelled = () => {
		showCropper.value = false
		form.logo = null
		tempImageFile.value = null
	}
</script>

<template>
	<ShadowBox class="bg-white">
		<form
			@submit.prevent="handleEvaluationEdit()"
			class=""
		>
			<HasPermission
				:name-permission="'create-medias-internal'"
				:entity-type="'App\\Models\\Chain'"
			>
				<p v-if="!isModifying">Imagen del logo: {{ chain.name }}</p>
				<img
					:src="chain.logo"
					class="w-24 h-24 rounded-full mb-2 mx-auto"
					:alt="`Imagen de logo ${form.name}`"
					v-if="!isModifying"
				/>
				<button
					class="text-blue-500 flex items-center w-full justify-center gap-2 hover:bg-white hover:text-blue-700"
					@click="
						() => {
							isModifying = true
						}
					"
					type="button"
					v-if="!isModifying"
				>
					Modificar
				</button>
				<StyledDropZone
					v-if="isModifying"
					v-model="form.logo"
					class="!w-[21.375 rem]"
					:accept-types="['image/png', 'image/jpg', 'image/jpeg']"
				/>
			</HasPermission>

			<!-- Image Cropper Modal -->
			<ImageCropper
				v-model:isVisible="showCropper"
				:imageSrc="imageToCrop"
				cropMode="square"
				:aspectRatioLocked="true"
				:uploading="updateChainLoading || createChainMediaLoading"
				@crop-applied="handleCropApplied"
				@crop-cancelled="handleCropCancelled"
			/>

			<HasPermission
				:name-permission="'update-internal'"
				:entity-type="'App\\Models\\Chain'"
			>
				<StyledInput
					v-model="form.name"
					label="Nombre de la cadena"
					placeholder="Nombre de la cadena"
					class="flex-1 mb-3"
					:is-required="true"
					:focus="true"
					:disabled="isCoincidence"
				/>
			</HasPermission>

			<StyledButton
				v-if="!isCoincidence"
				type="submit"
				class="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded ml-auto"
				:loading="updateChainLoading || createChainMediaLoading"
			>
				Editar cadena
			</StyledButton>

			<!-- <HasPermission
        :name-permission="'update-internal'"
        :entity-type="'App\\Models\\Chain'"
      > -->
			<CoincidencesList
				:data="chains?.data || []"
				:is-coincidence="isCoincidence"
				:loading-creating="updateChainLoading || createChainMediaLoading"
				:handle-action="handleEdit"
				:clear-state="
					() => {
						isCoincidence = false
					}
				"
				:action-modal="actionEdit"
			/>
			<!-- </HasPermission> -->
		</form>
	</ShadowBox>
</template>
